import { useInstantFormat } from '../instant-format';
/**
 * Event reminders in the browser (Outlook's reminder window, Google's notifications): while the webmail is open, the
 * own calendars are checked every half minute; a due reminder (the item's reminder and the platform's extra ones)
 * shows a toast with Dismiss and Snooze, and a desktop notification when the user allowed them and the tab is in the
 * background. Dismissed/snoozed reminders are remembered per browser (localStorage).
 */
import { useI18n } from '@platform/i18n';
import { toast } from '@platform/ui';
import { useEffect, useRef } from 'react';
import { api, type CalendarEvent } from '../api';

const STORE_KEY = 'platform.webmail.calendar.reminders';
const CHECK_MS = 30_000;
const FIRST_CHECK_MS = 10_000;
const LOAD_MS = 5 * 60_000;
export const SNOOZE_MINUTES = 5;
/** Look-ahead of the event load (largest reminder offset offered: one day). */
const AHEAD_MS = 2 * 86_400_000;

type Store = Record<string, number>; // key → dismissed (0) or snoozed until (ms)

function readStore(): Store {
  try {
    return JSON.parse(globalThis.localStorage?.getItem(STORE_KEY) ?? '{}') as Store;
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  try {
    globalThis.localStorage?.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // Not remembered: the reminder may come again after a reload.
  }
}

export interface DueReminder {
  key: string;
  event: CalendarEvent;
}

/** Reminders due at `now` (pure; tested). */
export function dueReminders(events: readonly CalendarEvent[], now: number, store: Store): DueReminder[] {
  return events.flatMap((event) => {
    if (event.masked || event.myResponse === 'declined' || event.isCancelled) return [];
    const start = Date.parse(event.start);
    if (Date.parse(event.end) <= now) return [];
    const offsets = [event.reminderMinutes, ...event.reminders].filter((m): m is number => m !== null);
    return offsets.flatMap((offset) => {
      const key = `${event.id}|${event.start}|${offset}`;
      const state = store[key];
      if (start - offset * 60_000 > now || state === 0 || (state !== undefined && state > now)) return [];
      return [{ key, event }];
    });
  });
}

/** Mounted once in the webmail shell while signed in. */
export function CalendarReminders({ active }: { active: boolean }) {
  const { t } = useI18n();
  const formatDate = useInstantFormat();
  const events = useRef<CalendarEvent[]>([]);
  const shown = useRef(new Set<string>());

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let loadedAt = 0;
    const load = async () => {
      const settings = await api.calendarSettings().catch(() => undefined);
      if (settings && !settings.remindersEnabled) return [];
      const calendars = await api.listCalendars();
      const ids = calendars.filter((c) => c.kind === 'own').map((c) => c.id);
      const now = Date.now();
      const results = await api.listEvents(ids, new Date(now - 86_400_000).toISOString(), new Date(now + AHEAD_MS).toISOString());
      return results.flatMap((r) => r.events);
    };
    const check = async () => {
      if (stopped) return;
      if (Date.now() - loadedAt > LOAD_MS) {
        loadedAt = Date.now();
        events.current = await load().catch(() => events.current);
      }
      const store = readStore();
      for (const { key, event } of dueReminders(events.current, Date.now(), store)) {
        if (shown.current.has(key)) continue;
        shown.current.add(key);
        const start = new Date(event.start);
        const when = start.getTime() <= Date.now() ? t('webmail.calendarUx.reminders.now') : t('webmail.calendarUx.reminders.at', { time: formatDate(start, { weekday: 'short', hour: 'numeric', minute: '2-digit' }) });
        const settle = (value: number) => {
          writeStore({ ...readStore(), [key]: value });
          shown.current.delete(key);
          toast.dismiss(key);
        };
        toast(event.subject || t('webmail.calendar.noTitle'), {
          id: key,
          description: [when, event.location].filter(Boolean).join(' · '),
          duration: Number.POSITIVE_INFINITY,
          action: { label: t('webmail.calendarUx.reminders.dismiss'), onClick: () => settle(0) },
          cancel: { label: t('webmail.calendarUx.reminders.snooze', { n: SNOOZE_MINUTES }), onClick: () => settle(Date.now() + SNOOZE_MINUTES * 60_000) },
        });
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
          try {
            new Notification(event.subject || t('webmail.calendar.noTitle'), { body: when, tag: key });
          } catch {
            // Some browsers only allow notifications from a service worker; the toast is still there.
          }
        }
      }
    };
    // First check a little after sign-in, so the page loads its own data first.
    const first = setTimeout(() => void check(), FIRST_CHECK_MS);
    const timer = setInterval(() => void check(), CHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
      clearTimeout(first);
    };
  }, [active, t, formatDate]);

  return null;
}
